import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { Field, getFormValues, reduxForm } from 'redux-form';
import { Row, Col, Container } from 'reactstrap';
import TimetableIcon from 'mdi-react/TimetableIcon';
import moment from 'moment';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import renderField from '../../../shared/components/custom/Field';
import validate from './validateTileForm';
import t, { partial } from '../../../util/translation/translation';
import renderDropZoneField from '../../../shared/components/form/DropZoneMS';
import renderSelectField from '../../../shared/components/form/Select';
import Api from '../../../util/api';
import useConfig from '../../../util/useConfig';
import FormBox from '../../../shared/components/custom/FormBox';
import {
  getReadableFormat,
  getFormatDimensions,
  isFilterable,
  hasAccess,
} from '../spotlightUtils';
import { getImageHash } from '../../../util/functions';
import renderDropZoneVideoField from '../../../shared/components/form/DropZoneVideo';

const UNLIMITED_DATE = '9999-12-31T00:00:00.000Z';

const p = partial('HighlightBannerForm');
const bp = partial('HighlightBannerPage');
const s = partial('shared');
const AVAILABLE_CONTENT = [
  {
    value: 'NEWS',
    label: s('news'),
  },
  {
    value: 'OFFERS',
    label: t('AppLayout.offersTile'),
  },
  {
    value: 'EVENTS',
    label: t('AppLayout.eventsTile'),
  },
  {
    value: 'EXTERNAL_LINK',
    label: s('externalLink'),
  },
  {
    value: 'WEBVIEW_LINK',
    label: s('webViewLink'),
  },
  {
    value: 'YOUTUBE_VIDEO',
    label: t('NewsForm.YOUTUBE_VIDEO_title'),
  },
  {
    value: 'CUSTOM_VIDEO',
    label: t('NewsForm.CUSTOM_VIDEO_title'),
  },
  {
    value: 'AUCTIONS',
    label: t('AppLayout.auctionsTile'),
  },
  {
    value: 'COMPANIES',
    label: s('companies'),
  },
  {
    value: 'PLAYERS',
    label: s('players'),
  },
  {
    value: 'NEWS_CATEGORY',
    label: s('newsCategory'),
  },
  {
    value: 'TEAMS',
    label: s('teams'),
  },
  {
    value: 'QUESTIONAIRES',
    label: t('questionaire.questionaires'),
  },
  {
    value: 'QUESTIONAIRE_CATEGORIES_LEADERBOARD',
    label: t(
      'questionaire.questionaireCategories.questionaireCategoriesLeaderboard'
    ),
  },
  {
    value: 'MATCHES',
    label: s('match'),
  },
  {
    value: 'MATCH_MVPS',
    label: s('MVP'),
  },
  {
    value: 'GOAL_SPONSOR',
    label: t('goalSponsor.goalSponsor'),
  },
];
const getContentType = (type) => {
  return AVAILABLE_CONTENT.find((item) => item.value === type);
};
const getCropType = (format, size) => {
  if (format === 'BANNER') {
    return `SPOTLIGHT_${format}_${size}_CROP`;
  }
  if (format === 'TEXT_BANNER') {
    return 'SPOTLIGHT_CIRCLE_CROP';
  }
  return `SPOTLIGHT_${format}_CROP`;
};
const EMPTY_TEAMS = [];
const EMPTY_AVAILABLE_CONTENT = {
  news: [],
  events: [],
  offers: [],
  auctions: [],
  companies: [],
  players: [],
  news_category: [],
  matchCenter: [],
  teams: [],
  questionaires: [],
  questionaire_categories_leaderboard: [],
  matches: [],
  match_mvps: [],
  goal_sponsor: [],
};

const SpotlightTileForm = ({
  handleSubmit,
  initialize,
  entity,
  change,
  format,
  size,
  setSelectedEntityAccess,
  groupsLevelsTiers,
  descriptionAvailable,
  formValues,
  onLoadingChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [availableContent, setAvailableContent] = useState(
    EMPTY_AVAILABLE_CONTENT
  );
  const [contentLoaded, setContentLoaded] = useState(false);
  const [contentTypes, setContentTypes] = useState([]);
  const [auctionItems, setAuctionItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [content, setContent] = useState(null);
  const initializedEntityKeyRef = useRef(null);
  const { id: clubId, teams: configTeams } = useConfig();
  const teams = configTeams || EMPTY_TEAMS;
  const teamIds = useMemo(() => teams.map((team) => team.id), [teams]);
  const teamsKey = useMemo(() => teamIds.join(','), [teamIds]);
  const teamContentOptions = useMemo(
    () =>
      teams.map((item) => ({
        label: item.name,
        value: item.id,
      })),
    [teams]
  );
  const imageFormat = format || 'SQUARE';
  const imageSize = size || 'MEDIUM';
  const CONTENT_LIST = useMemo(
    () =>
      AVAILABLE_CONTENT.filter((item) => {
        if (!['PORTRAIT', 'LANDSCAPE'].includes(format)) {
          if (['YOUTUBE_VIDEO'].includes(item.value)) {
            return false;
          }
        }
        return true;
      }),
    [format]
  );
  const fetchWholeContent = useCallback(
    async (setInnerState = true, auctionHouseId = null) => {
      try {
        const [
          { data: eventsData },
          { data: newsData },
          { data: offersData },
          { data: auctionHouseData },
          auctionHouseItems,
          resCompanies,
          resNewsCategories,
          teamPlayersResults,
          questionaireOptions,
          questionaireCategoryResults,
          {
            data: { matches: matchData },
          },
          { data: goalSponsorData },
        ] = await Promise.all([
          Api.events.all(),
          Api.news.getNews(
            { page: 1, take: 100 },
            { orderBy: 'publicationDate', direction: 'DESC' }
          ),
          Api.offers.fetchAllOffers(clubId, 1, 50),
          Api.auctions.getAuctionHouses(
            { page: 1, take: 100 },
            { orderBy: 'createdAt', direction: 'DESC' }
          ),
          auctionHouseId
            ? Api.auctions.getAuctionItemsForHouse(auctionHouseId)
            : [],
          Api.companies.getCompaniesForClub(),
          Api.news.getNewsCategories(),
          Promise.all(
            teamIds.map((teamId) =>
              Api.players
                .getPLayersFromNtb(teamId)
                .then((res) => res.data.map((item) => ({ ...item, teamId })))
            )
          ),
          Api.questionaire.getQuestionaireOptions(),
          Api.questionaire.categories.getQuestionaireCategories(),
          Api.matchCenter.getUpcomingMatches(1, 100, teamIds, false),
          Api.goalSponsor.subscriptionPlans.getSubscriptionPlans(
            { page: 1, take: 100 },
            {
              orderBy: 'createdAt',
              direction: 'DESC',
            }
          ),
        ]);
        const sortedEvents = eventsData.sort((a, b) =>
          moment(b.startDate).diff(moment(a.startDate))
        );
        const sortedCompanies = resCompanies
          .filter((item) => item.company.name)
          .sort((a, b) =>
            a.company.name.trim().localeCompare(b.company.name.trim())
          );
        const mappedEvents = [
          {
            value: null,
            label: `${t('AppLayout.eventsTile')} ${s('list')}`,
          },
          ...sortedEvents.map((item) => ({
            label: item.title,
            value: item.id,
          })),
        ];
        const mappedOffers = [
          {
            value: null,
            label: `${t('AppLayout.offersTile')} ${s('list')}`,
          },
          ...offersData.offers.map((item) => ({
            label: item.title,
            value: item.id,
          })),
        ];
        const mappedNews = [
          {
            value: null,
            label: `${t('AppLayout.newsTile')} ${s('list')}`,
          },
          ...newsData.map((item) => ({
            label: item.title,
            value: item.id,
          })),
        ];
        const mappedAuctions = [
          {
            value: null,
            label: `${t('auctionObjectsShared.auctionHouse')} ${s('list')}`,
          },
          ...auctionHouseData.map((item) => ({
            label: item.name,
            value: item.id,
          })),
        ];
        const mappedCompanies = [
          {
            value: null,
            label: `${s('companies')} ${s('list')}`,
          },
          ...sortedCompanies.map((item) => ({
            label: item.company.name,
            value: item.id,
          })),
        ];
        const mappedNewsCategories = resNewsCategories.map((item) => ({
          label: item.name,
          value: item.id,
        }));
        const mappedPlayers = teamPlayersResults.flatMap((teamPlayers) =>
          teamPlayers.map((item) => ({
            label: item.fullname,
            value: `${item.teamId}@${item.id}`,
          }))
        );
        const mappedAuctionHouseItems = [
          {
            label: bp('auctionHousesPage'),
            value: null,
          },
          ...auctionHouseItems.map((item) => ({
            label: item.name,
            value: item.id,
          })),
        ];
        const mappedQuestionaires = questionaireOptions.map((item) => ({
          label: item.title,
          value: item.id,
        }));
        const mappedQuestionaireCategories = questionaireCategoryResults.map(
          (item) => ({
            label: item.title,
            value: item.id,
          })
        );
        const mappedMatches = matchData.map((item) => {
          const foundTeamId = teamIds.find(
            (teamId) =>
              teamId === item?.homeTeam?.id || teamId === item?.awayTeam?.id
          );
          return {
            label: `${item?.league?.name} - ${item?.homeTeam?.name} - ${item?.awayTeam?.name}`,
            value: `${foundTeamId}@${item.id}`,
          };
        });
        const mappedMatchMvps = mappedMatches.map((item) => ({
          ...item,
          value: `${item.value}@MVP`,
        }));
        const mappedGoalSponsor = goalSponsorData.map((item) => ({
          label: item.name,
          value: item.id,
        }));
        // find team of the match by comparing to our teams and contruct value out of it
        const combinedData = {
          news: mappedNews,
          events: mappedEvents,
          offers: mappedOffers,
          auctions: mappedAuctions,
          companies: mappedCompanies,
          news_category: mappedNewsCategories,
          players: mappedPlayers,
          teams: teamContentOptions,
          questionaires: mappedQuestionaires,
          questionaire_categories_leaderboard: mappedQuestionaireCategories,
          matches: mappedMatches,
          match_mvps: mappedMatchMvps,
          goal_sponsor: mappedGoalSponsor,
        };
        if (setInnerState) {
          setAvailableContent(combinedData);
          setContentLoaded(true);
        }
        setCompanies(mappedCompanies.filter((comp) => comp.value !== null));
        return { ...combinedData, auctionHouseItems: mappedAuctionHouseItems };
      } catch (e) {
        toast.error(e || s('somethingWentWrong'));
        if (setInnerState) {
          setAvailableContent(EMPTY_AVAILABLE_CONTENT);
          setContentLoaded(true);
        }
        return { ...EMPTY_AVAILABLE_CONTENT, auctionHouseItems: [] };
      }
    },
    [clubId, teamIds, teamContentOptions]
  );
  const mapOfferAccess = useCallback((accessArray, field) => {
    let key = null;
    switch (field) {
      case 'groups':
        key = 'name';
        break;
      case 'levels':
        key = 'name';
        break;
      case 'tiers':
        key = 'tierId';
        break;
      default:
        break;
    }

    return accessArray.length === 0 ||
      accessArray?.find((item) => item.name === 'All users')
      ? null
      : accessArray.find((item) => item.name === `All ${field}`)
        ? []
        : accessArray.map((item) => item[key]);
  }, []);

  const mapEventAccess = useCallback(
    (accessArray, field, searchField) => {
      const mappedData =
        accessArray === null || accessArray?.length === 0
          ? accessArray
          : accessArray.map((accessName) => {
              const foundRecord = groupsLevelsTiers[field].find(
                (item) => item[searchField] === accessName
              );
              return foundRecord?.value;
            });
      return mappedData;
    },
    [groupsLevelsTiers]
  );
  const fetchEntityAccess = useCallback(
    async (entityId, contentType) => {
      switch (contentType) {
        case 'NEWS':
          return Api.news.getNewsAccess(entityId);
        case 'QUESTIONAIRES':
          return Api.questionaire.getQuestionaireAccess(entityId);
        case 'OFFERS':
          return Api.offers
            .fetchSingleOffer(entityId, clubId)
            .then(({ data }) => {
              const groups = mapOfferAccess(data.groups, 'groups');
              const levels = mapOfferAccess(data.levels, 'levels');
              const tiers = mapOfferAccess(data.tiers, 'tiers');
              return { groups, levels, tiers, noAccessView: data.isVisible };
            });
        case 'EVENTS':
          return Api.events.show(entityId).then(({ data }) => {
            const groups = mapEventAccess(
              data.accessCategories,
              'groups',
              'value'
            );
            const levels = mapEventAccess(data.accessLevels, 'levels', 'label');
            const tiers = mapEventAccess(data.accessTiers, 'tiers', 'value');
            return { groups, levels, tiers, noAccessView: data.feedVisibility };
          });
        case 'AUCTIONS': {
          const [houseId] = entityId.split('|');
          const [auctionHouseAccess, auctionItemsData] = await Promise.all([
            Api.auctions.getAuctionHouseAccess(houseId),
            Api.auctions.getAuctionItemsForHouse(houseId),
          ]);
          const mappedItems = [
            {
              label: 'Main page',
              value: null,
            },
            ...auctionItemsData.map((item) => ({
              label: item.name,
              value: item.id,
            })),
          ];
          setAuctionItems(mappedItems);
          return auctionHouseAccess;
        }
        default:
          return null;
      }
    },
    [clubId, mapEventAccess, mapOfferAccess]
  );
  const handleContentTypeChange = useCallback(
    async (data, contentType) => {
      if (hasAccess(contentType)) {
        const entityId = data?.value;
        if (!entityId) {
          setSelectedEntityAccess({
            groups: null,
            levels: null,
            tiers: null,
            segments: [],
            noAccessView: true,
            selectedContent: { type: contentType, contentId: entityId },
          });
        } else {
          const result = await fetchEntityAccess(entityId, contentType);
          if (!result) {
            setSelectedEntityAccess({
              groups: null,
              levels: null,
              tiers: null,
              segments: [],
              noAccessView: true,
              selectedContent: { type: contentType, contentId: entityId },
            });
            return;
          }
          const { groups, levels, tiers, noAccessView } = result;
          setSelectedEntityAccess({
            groups,
            levels,
            tiers,
            segments: [],
            noAccessView,
            selectedContent: { type: contentType, contentId: entityId },
          });
        }
      } else {
        setSelectedEntityAccess({
          groups: null,
          levels: null,
          tiers: null,
          segments: [],
          noAccessView: true,
          selectedContent: { type: contentType, contentId: data?.value },
          isEditable: true,
        });
      }
    },
    [fetchEntityAccess, setSelectedEntityAccess]
  );
  const entityInitializationKey = entity
    ? `${entity.id || entity.tileId || ''}-${clubId || ''}-${teamsKey}-${format || ''}`
    : null;
  useEffect(() => {
    if (!entity || !contentLoaded) {
      return undefined;
    }
    if (initializedEntityKeyRef.current === entityInitializationKey) {
      return undefined;
    }

    let cancelled = false;
    const initializeEntity = async () => {
      let foundContent = entity.contentId || '';
      let foundSubContent = null;
      const selectedCompany = entity.companyId
        ? companies.find((com) => com.value === entity.companyId)
        : null;
      setLoading(true);
      if (isFilterable(entity.contentType)) {
        const contentOptions =
          availableContent[entity.contentType.toLowerCase()] || [];
        if (entity.contentType === 'AUCTIONS') {
          const [houseId, itemId] = foundContent.split('|');
          const auctionHouseItems =
            await Api.auctions.getAuctionItemsForHouse(houseId);
          const mappedAuctionHouseItems = [
            {
              label: bp('auctionHousesPage'),
              value: null,
            },
            ...auctionHouseItems.map((item) => ({
              label: item.name,
              value: item.id,
            })),
          ];

          if (cancelled) {
            return;
          }

          setAuctionItems(mappedAuctionHouseItems);
          foundContent =
            contentOptions.find((item) => item.value === houseId)?.label ||
            contentOptions.find((item) => item.value === null)?.label;
          const isAuctionHouseList =
            foundContent ===
            `${t('auctionObjectsShared.auctionHouse')} ${s('list')}`;
          foundSubContent = isAuctionHouseList
            ? null
            : {
                label:
                  mappedAuctionHouseItems.find((item) => item.value === itemId)
                    ?.label ||
                  contentOptions.find((item) => item.value === null)?.label,
                value: itemId,
              };
        } else {
          foundContent =
            contentOptions.find((item) => item.value === entity.contentId)
              ?.label ||
            contentOptions.find((item) => item.value === null)?.label;
        }
      }

      if (cancelled) {
        return;
      }

      initialize({
        title: entity.title,
        description: entity.description,
        publicationDate: moment(entity.publicationDate).format(
          'YYYY-MM-DDTHH:mm'
        ),
        expirationDate:
          entity.expirationDate !== UNLIMITED_DATE
            ? moment(entity.expirationDate).format('YYYY-MM-DDTHH:mm')
            : null,
        content: getContentType(entity.contentType),
        contentType: isFilterable(entity.contentType)
          ? {
              label: foundContent,
              value: entity.contentId,
            }
          : entity.contentId,
        subContentType: foundSubContent,
        headingImage:
          format === undefined
            ? undefined
            : {
                name: 'tile image',
                preview: entity.imageUrl,
                croppedImage: entity.imageUrl,
                hash: getImageHash(entity.imageUrl),
              },
        headingVideo: entity.contentId
          ? {
              preview: entity.contentId,
            }
          : null,
        company: selectedCompany,
      });
      setContentTypes(availableContent[entity.contentType.toLowerCase()] || []);
      setContent(entity.contentType);
      await handleContentTypeChange(
        { value: entity?.contentId || null },
        entity.contentType
      );

      if (!cancelled) {
        initializedEntityKeyRef.current = entityInitializationKey;
        setLoading(false);
      }
    };

    initializeEntity();

    return () => {
      cancelled = true;
    };
  }, [
    availableContent,
    contentLoaded,
    entity,
    entityInitializationKey,
    format,
    handleContentTypeChange,
    initialize,
  ]);
  useEffect(() => {
    setContentLoaded(false);
    fetchWholeContent();
  }, [fetchWholeContent]);

  const changeContentType = async (contentType) => {
    if (isFilterable(contentType)) {
      setContentTypes(availableContent[contentType.toLowerCase()]);
      if (
        [
          'AUCTIONS',
          'NEWS_CATEGORY',
          'PLAYERS',
          'TEAMS',
          'QUESTIONAIRE_CATEGORIES_LEADERBOARD',
          'MATCHES',
          'MATCH_MVPS',
          'GOAL_SPONSOR',
        ].includes(contentType)
      ) {
        change('contentType', null);
      } else {
        change('contentType', {
          label: `${t(`AppLayout.${contentType.toLowerCase()}Tile`)} ${s('list')}`,
          value: null,
        });
      }

      setSelectedEntityAccess({
        groups: null,
        levels: null,
        tiers: null,
        noAccessView: true,
        selectedContent: { type: contentType, contentId: null },
      });
    } else {
      setSelectedEntityAccess({
        groups: null,
        levels: null,
        tiers: null,
        noAccessView: true,
        selectedContent: { type: contentType, contentId: null },
        isEditable: true,
      });
    }
  };
  const handleContentChange = (data) => {
    setContent(data.value);
    change('contentType', null);
    change('subContentType', null);
    changeContentType(data.value);
  };
  const contentTypePlaceholder = (_cont) => {
    switch (_cont) {
      case 'EXTERNAL_LINK':
        return s('externalLink');
      case 'WEBVIEW_LINK':
        return s('webViewLink');
      case 'YOUTUBE_VIDEO':
        return t('NewsForm.youtubeVideoID');
      default:
        return '';
    }
  };
  const entityInitializing =
    !!entity &&
    (!contentLoaded ||
      initializedEntityKeyRef.current !== entityInitializationKey);
  // Report our own readiness up to the page instead of rendering a second,
  // overlapping full-screen <Loading> overlay on top of the page's own one.
  useEffect(() => {
    onLoadingChange(loading || entityInitializing);
  }, [loading, entityInitializing, onLoadingChange]);
  return (
    <form className="form" onSubmit={handleSubmit}>
      <Col md={12} lg={12} xl={12} style={{ marginBottom: '10px', padding: 0 }}>
        <FormBox title={s('details')}>
          <div style={{ padding: '10px' }}>
            <Container className="">
              <Row>
                <Col md={content === 'AUCTIONS' ? 4 : 6}>
                  <div className="form__form-group">
                    <span className="form-header">{p('selectContent')}*</span>
                    <Field
                      name="content"
                      placeholder={p('selectContent')}
                      options={CONTENT_LIST}
                      component={renderSelectField}
                      onChange={handleContentChange}
                    />
                  </div>
                </Col>
                {content !== 'CUSTOM_VIDEO' ? (
                  <Col md={content === 'AUCTIONS' ? 4 : 6}>
                    <div className="form__form-group">
                      <span className="form-header">
                        {p('selectContentType')}*
                      </span>
                      {!isFilterable(content) ? (
                        <Field
                          name="contentType"
                          component={renderField}
                          placeholder={contentTypePlaceholder(content)}
                        />
                      ) : (
                        <Field
                          name="contentType"
                          placeholder={p('selectContentType')}
                          options={contentTypes}
                          component={renderSelectField}
                          disabled={!content}
                          onChange={(e) => {
                            handleContentTypeChange(e, content);
                            change('subContentType', null);
                          }}
                        />
                      )}
                    </div>
                  </Col>
                ) : (
                  <Col md={6}>
                    <div className="form__form-group">
                      <span className="form-header">{s('video')}*</span>
                      <Field
                        name="headingVideo"
                        component={renderDropZoneVideoField}
                      />
                    </div>
                  </Col>
                )}
                {content === 'AUCTIONS' ? (
                  <Col md={content === 'AUCTIONS' ? 4 : 6}>
                    <div className="form__form-group">
                      <span className="form-header">
                        {p('selectContentType')}*
                      </span>
                      <Field
                        disabled={!formValues?.contentType?.value}
                        name="subContentType"
                        placeholder={p('selectContentType')}
                        options={auctionItems}
                        component={renderSelectField}
                      />
                    </div>
                  </Col>
                ) : null}
              </Row>
              <Row>
                <Col md={!descriptionAvailable ? 12 : 6}>
                  <div className="form__form-group">
                    <span className="form-header">{s('title')}*</span>
                    <Field
                      name="title"
                      component={renderField}
                      placeholder={s('title')}
                    />
                  </div>
                </Col>
                {descriptionAvailable ? (
                  <Col md={6}>
                    <div className="form__form-group">
                      <span className="form-header">{bp('description')}*</span>
                      <Field
                        name="description"
                        component={renderField}
                        placeholder={s('description')}
                      />
                    </div>
                  </Col>
                ) : null}
              </Row>
              <Row>
                <Col>
                  <div className="form__form-group">
                    <span className="form">{s('publicationDate')}</span>
                    <div className="form__form-group-field">
                      <div className="form__form-group-icon">
                        <TimetableIcon />
                      </div>
                      <Field
                        name="publicationDate"
                        component={renderField}
                        type="datetime-local"
                      />
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="form__form-group">
                    <span className="form">{s('expirationDate')}</span>
                    <div className="form__form-group-field">
                      <div className="form__form-group-icon">
                        <TimetableIcon />
                      </div>
                      <Field
                        name="expirationDate"
                        component={renderField}
                        type="datetime-local"
                      />
                    </div>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <div className="form__form-group">
                    <span className="form-header">
                      {s('sponsor')} - {s('tracking')}
                    </span>
                    <Field
                      name="company"
                      placeholder="Select a partner"
                      options={companies}
                      component={renderSelectField}
                    />
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        </FormBox>
      </Col>
      <Col md={12} lg={12} xl={12} style={{ marginBottom: '10px', padding: 0 }}>
        <FormBox
          title={`${s('image')} ${content === 'YOUTUBE_VIDEO' || format === 'TEXT_BANNER' ? `(${s('optional')})` : ''}`}
        >
          <Col>
            <div className="form__form-group">
              <Field
                saveBase64Cropped
                name="headingImage"
                maxSize={3}
                component={renderDropZoneField}
                crop={getCropType(imageFormat, imageSize)}
                displayCrop
              />
              <span className="form-header">
                <strong>{getReadableFormat(imageFormat)}</strong>{' '}
                {getFormatDimensions(imageFormat, true, imageSize)}
              </span>
            </div>
          </Col>
        </FormBox>
      </Col>
    </form>
  );
};

const reduxF = reduxForm({
  form: 'tile_form',
  validate,
});

const mapStateToProps = (state) => ({
  formValues: getFormValues('tile_form')(state),
});
export default connect(mapStateToProps)(reduxF(SpotlightTileForm));
